import { MouseEvent, PropsWithChildren, useCallback } from "react";
import { classes } from "../react";

interface ExpandButtonProps extends ExpandIconProps {
    /**
     * Callback when the button is clicked.
     * @param expanded The next value.
     */
    onToggle?: (expanded: boolean) => void;
    /**
     * Whether the button is disabled.
     */
    disabled?: boolean;
}

export function ExpandButton({
    children,
    className,
    disabled,
    expanded,
    onToggle,
    ...props
}: PropsWithChildren<ExpandButtonProps>) {
    const handleClick = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            onToggle?.(!expanded);
        },
        [expanded, onToggle]
    );

    return (
        <button
            type="button"
            className={classes("omnia-expand-button btn btn-icon transparent", className, {
                "omnia-expand-button--has-label": !!children,
            })}
            onClick={!disabled ? handleClick : undefined}
            disabled={disabled}
        >
            <ExpandIcon expanded={expanded} {...props} />
            {children ? <span className="omnia-expand-button--label">{children}</span> : null}
        </button>
    );
}

interface ExpandIconProps {
    className?: string;
    /**
     * Whether the icon is currently "expanded" or "collapsed".
     */
    expanded?: boolean;

    /**
     * The "expanded" icon CSS class.
     */
    iconExpanded?: string;

    /**
     * The "collapsed" icon CSS class.
     */
    iconCollapsed?: string;
}

export function ExpandIcon({
    className,
    expanded,
    iconExpanded = "fas fa-chevron-circle-up",
    iconCollapsed = "fas fa-chevron-circle-down",
}: ExpandIconProps) {
    return (
        <i
            className={classes(
                "omnia-expand-icon",
                {
                    [iconExpanded]: expanded,
                    [iconCollapsed]: !expanded,
                },
                className
            )}
        />
    );
}
